/* eslint-disable react-refresh/only-export-components */
import clsx from 'clsx'
import { useDrillDown } from '@/components/dashboards/useDrillDown'
import { drillForIds, type ActivityItem } from '@/services/dashboardAnalytics'

function stamp(value: string | null): string {
  if (!value) return '--'
  const at = Date.parse(value)
  if (!Number.isFinite(at)) return '--'
  return new Date(at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
}

function dayKey(value: string | null): string {
  if (!value) return 'Undated'
  const at = Date.parse(value)
  if (!Number.isFinite(at)) return 'Undated'
  return new Date(at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
}

/** Activity text without file hashes, UUIDs or long hex ids — machine text a
 *  client must never read. The server already writes plain sentences; this is
 *  the guard for anything older or unmapped. */
export function plainActivity(text: string | null | undefined): string {
  if (!text) return '--'
  const cleaned = text
    .replace(/\(?\s*sha-?256[:\s]*[0-9a-f]{6,}\s*\)?/gi, '')
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '')
    .replace(/\b(?=[0-9a-f]*\d)[0-9a-f]{10,}\b/gi, '')
    .replace(/\(\s*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
    .replace(/[·—\s-]+$/, '')
  return cleaned || '--'
}

const KIND_DOT: Record<string, string> = {
  field: 'bg-amber-300/80',
}

/**
 * The newest recorded events on the person's deals, grouped by day. Each
 * line is the event's own text (field: old → new, or the event's own verb) —
 * nothing is summarised or inferred. A deal name opens that deal.
 */
export default function ActivityTimeline({ items, className }: { items: readonly ActivityItem[]; className?: string }) {
  const { open } = useDrillDown()
  if (items.length === 0) {
    return <p className={clsx('px-1 py-3 text-[11px] text-stone-500', className)}>No recorded event on these deals.</p>
  }
  const groups: Array<{ day: string; items: ActivityItem[] }> = []
  for (const item of items) {
    const day = dayKey(item.at)
    const last = groups[groups.length - 1]
    if (last && last.day === day) last.items.push(item)
    else groups.push({ day, items: [item] })
  }
  return (
    <ol className={clsx('space-y-2', className)}>
      {groups.map((group) => (
        <li key={group.day}>
          <div className="sticky top-0 z-[1] bg-[#0b0b0b]/95 py-0.5 font-mono text-[8.5px] uppercase tracking-[0.18em] text-stone-500">
            {group.day}
          </div>
          <ol className="mt-1 space-y-1.5 border-l border-amber-400/10 pl-2.5">
            {group.items.map((item, index) => (
              <li key={`${item.at}-${item.dealId}-${index}`} className="relative">
                <span
                  className={clsx('absolute -left-[13px] top-1.5 h-1.5 w-1.5 rounded-full', KIND_DOT[item.kind ?? ''] ?? 'bg-stone-500')}
                  aria-hidden="true"
                />
                <div className="flex items-baseline gap-1.5">
                  <span className="flex-shrink-0 font-mono text-[9px] tabular-nums text-stone-600">{stamp(item.at).split(', ').pop()}</span>
                  {item.dealId ? (
                    <button
                      type="button"
                      onClick={() => open(drillForIds([item.dealId as string], item.dealName ?? item.dealId ?? 'Deal'))}
                      className="min-w-0 truncate text-left text-[10.5px] font-medium text-stone-200 hover:text-amber-200"
                      title={item.dealName ?? item.dealId ?? undefined}
                    >
                      {item.dealName ?? item.dealId}
                    </button>
                  ) : (
                    <span className="text-[10.5px] text-stone-400">—</span>
                  )}
                </div>
                <p className="break-words text-[10px] leading-snug text-stone-400">{plainActivity(item.summary)}</p>
                {item.actor && <p className="truncate text-[9px] text-stone-600">{item.actor}</p>}
              </li>
            ))}
          </ol>
        </li>
      ))}
    </ol>
  )
}
